/**
 * Multi-Select Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for multi-select columns.
 * Displays values as bordered Tags; editable uses a multi-select ComboBox.
 */

import { memo } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { TagsWithOverflow } from 'components/tags/TagsWithOverflow';
import type { ComboBoxApi, ComboBoxOption } from 'components/inputs/types';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import type { MultiSelectValue } from '../../../components/types';
import { CellContainer } from '../../tableStyles';
import { EditableMultiselectCell } from './EditableMultiselectCell';
import { getCellValue, parseMultiSelectValue } from '../cellValue';
import { getLookupApiFn } from '../multiselect';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get multi-select values from cell context
 * Uses shared getCellValue (respects accessorFn) and shared parseMultiSelectValue
 */
const getCellMultiSelectValues = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): MultiSelectValue[] => {
  const value = getCellValue(context, context.column.id);
  return parseMultiSelectValue(value);
};

/**
 * Convert MultiSelectValue array to display string for tooltip
 */
const getDisplayString = (values: MultiSelectValue[]): string => {
  return values.map(v => v.label).join(', ');
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized read-only multiselect cell component
 */
const MultiselectReadOnlyCell = memo(function MultiselectReadOnlyCell({
  values,
  columnWidth,
}: {
  values: MultiSelectValue[];
  columnWidth?: number;
}) {
  if (values.length === 0) {
    return <CellContainer> </CellContainer>;
  }

  return (
    <CellContainer>
      <TagsWithOverflow values={values} columnWidth={columnWidth} />
    </CellContainer>
  );
});

/**
 * Multi-select read-only renderer - displays values as bordered Tags
 * Uses getValue() to support nested accessors
 * Includes internal Tooltip for consistent hover behavior
 */
export const multiselectReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    // Use getCellMultiSelectValues to respect accessorFn (important for custom fields)
    const values = getCellMultiSelectValues(cellContext);

    return <MultiselectReadOnlyCell values={values} columnWidth={cellContext.column.getSize()} />;
  },
});

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Multi-select editable renderer - supports API-backed (registry) and static options
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 *
 * When API is resolved (e.g. categories from registry): uses MultiselectAutocomplete with api + multiple.
 * When only static col.options: uses MultiselectAutocomplete with static options.
 * When neither: renders read-only and meta.editable false.
 */
export const multiselectEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  // Resolve API: explicit col.api > registry by column id (same as dropdown and form multiselect)
  const resolvedApi = (col.api ||
    (getLookupApiFn ? (getLookupApiFn(col.id) as ComboBoxApi | null) : null)) as ComboBoxApi | null;
  const hasStaticOptions = Boolean(col.options && col.options.length > 0);
  const hasNoDataSource = !resolvedApi && !hasStaticOptions;

  const editableProp = col.editable;

  // No data source: render read-only and disable edit (same fallback as dropdown)
  if (hasNoDataSource) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const values = getCellMultiSelectValues(context);
        return (
          <CellContainer>
            {values.length === 0 ? null : (
              <TagsWithOverflow values={values} columnWidth={context.column.getSize()} />
            )}
          </CellContainer>
        );
      },
      meta: {
        editable: false,
      },
    };
  }

  return {
    cell: (context: CellContext<T, unknown>) => {
      const values = getCellMultiSelectValues(context);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      const readOnlyDisplay = (
        <CellContainer>
          {values.length === 0 ? null : (
            <TagsWithOverflow values={values} columnWidth={context.column.getSize()} />
          )}
        </CellContainer>
      );

      if (!isRowEditable) {
        return readOnlyDisplay;
      }

      const currentValues: ComboBoxOption[] = values.map(v => ({ id: v.id, label: v.label }));

      return (
        <EditableMultiselectCell<T>
          context={context}
          value={currentValues}
          onUpdate={(row, update) => col.onUpdate?.(row, update)}
          placeholder=""
          api={resolvedApi}
          options={col.options ?? []}
          columnId={col.id}
          required={'required' in col ? !!col.required : undefined}
          columnLabel={col.label}
          disabled={!isRowEditable}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const multiselectReadOnly: ColumnRenderer = multiselectReadOnlyRenderer;

/** Editable renderer with proper typing */
export const multiselectEditable: ColumnRenderer = multiselectEditableRenderer;
